import { Fragment } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';

import { DOCUMENTOS, LISTA_DE_DOCUMENTOS, PRODUTO } from '@/data/juridico';
import type { Bloco, IdDoDocumento } from '@/data/juridico';

const ENDERECO_OU_EMAIL = /(https:\/\/\S+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/;

const LINK: CSSProperties = { color: 'var(--text-link)', fontWeight: 600, overflowWrap: 'anywhere' };

/**
 * Endereço e e-mail escritos no texto viram link. A pontuação que fecha a frase não entra no
 * link, e o endereço dos próprios documentos vira link interno: a página abre também no
 * domínio antigo, e um link fixo em app.formulamed.com.br tiraria a pessoa de onde está.
 */
function comLinks(texto: string): ReactNode[] {
    return texto.split(ENDERECO_OU_EMAIL).map((parte, i) => {
        if (i % 2 === 0) return parte;
        const sobra = /[.,;:)]+$/.exec(parte)?.[0] ?? '';
        const alvo = sobra ? parte.slice(0, -sobra.length) : parte;
        const interno = alvo.startsWith(`${PRODUTO.site}/`) ? alvo.slice(PRODUTO.site.length) : null;
        let link: ReactNode;
        if (interno) link = <Link to={interno} style={LINK}>{alvo}</Link>;
        else if (alvo.startsWith('https://')) link = <a href={alvo} target="_blank" rel="noopener noreferrer" style={LINK}>{alvo}</a>;
        else link = <a href={`mailto:${alvo}`} style={LINK}>{alvo}</a>;
        return <Fragment key={`${i}-${alvo}`}>{link}{sobra}</Fragment>;
    });
}

const TEXTO: CSSProperties = { margin: 0, fontSize: 14.5, lineHeight: '24px', color: 'var(--text-body)', maxWidth: '68ch' };
const CELULA: CSSProperties = { padding: '10px 12px', textAlign: 'left', verticalAlign: 'top', borderBottom: '1px solid var(--border-subtle)', overflowWrap: 'anywhere' };

function BlocoDoDocumento({ bloco }: { bloco: Bloco }) {
    if (bloco.tipo === 'p') return <p style={TEXTO}>{comLinks(bloco.texto)}</p>;
    if (bloco.tipo === 'lista') {
        return (
            <ul style={{ ...TEXTO, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bloco.itens.map((item) => <li key={item}>{comLinks(item)}</li>)}
            </ul>
        );
    }
    /* Sem contêiner que rola de lado: o texto das células quebra, e a tabela cabe em 320 px. */
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, lineHeight: '20px', color: 'var(--text-body)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            <thead>
                <tr style={{ background: 'var(--surface-secondary)' }}>
                    {bloco.colunas.map((c) => <th key={c} scope="col" style={{ ...CELULA, fontWeight: 600, color: 'var(--text-primary)' }}>{c}</th>)}
                </tr>
            </thead>
            <tbody>
                {bloco.linhas.map((linha) => (
                    <tr key={linha.join('|')}>
                        {linha.map((celula, i) => (
                            i === 0
                                ? <th key={`${i}-${celula}`} scope="row" style={{ ...CELULA, fontWeight: 600, color: 'var(--text-primary)' }}>{comLinks(celula)}</th>
                                : <td key={`${i}-${celula}`} style={CELULA}>{comLinks(celula)}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export interface ConteudoDoDocumentoProps {
    id: IdDoDocumento;
    /** 2 na página própria; 3 dentro de um modal, que já tem o título como cabeçalho. */
    nivelDoTitulo?: 2 | 3;
}

/** As seções numeradas de um documento — a página e o modal do cadastro mostram o mesmo texto. */
export function ConteudoDoDocumento({ id, nivelDoTitulo = 2 }: ConteudoDoDocumentoProps) {
    const Titulo = nivelDoTitulo === 2 ? 'h2' : 'h3';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
            {DOCUMENTOS[id].secoes.map((secao, i) => (
                <section key={secao.id} aria-labelledby={secao.id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Titulo id={secao.id} style={{ margin: 0, scrollMarginTop: 16, fontFamily: 'var(--font-display)', fontSize: nivelDoTitulo === 2 ? 19 : 16, lineHeight: 1.3, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {i + 1}. {secao.titulo}
                    </Titulo>
                    {secao.blocos.map((bloco, j) => <BlocoDoDocumento key={`${secao.id}-${j}`} bloco={bloco} />)}
                </section>
            ))}
        </div>
    );
}

/** Os três documentos, para rodapés: tela de entrar, cadastro e as próprias páginas. */
export function LinksLegais({ atual }: { atual?: IdDoDocumento }) {
    return (
        <nav aria-label="Documentos legais">
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 16px', fontSize: 12.5 }}>
                {LISTA_DE_DOCUMENTOS.map((d) => (
                    <li key={d.id}>
                        <Link
                            to={d.caminho} aria-current={d.id === atual ? 'page' : undefined}
                            style={{ color: d.id === atual ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: d.id === atual ? 600 : 500 }}
                        >
                            {d.titulo}
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
